<script setup lang="ts">
import type {
  EditorialGallerySlideshowProps,
  galleryCard,
} from './EditorialGallerySlideshow.props'
import { v4 as uuidv4 } from 'uuid'
import type { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel'

const props = defineProps<EditorialGallerySlideshowProps>()

const GALLERY_SLIDE_SHOW_ID = `gallery-slideshow-${uuidv4()}`

const { ts } = useI18n()
const { openDialog, isDialogOpen } = useDialog(GALLERY_SLIDE_SHOW_ID)

const clickedSlide = ref(0)

const emblaApi = ref<EmblaCarouselType | null>(null)

const options = computed(
  (): EmblaOptionsType => ({
    loop: false,
    skipSnaps: true,
    startIndex: clickedSlide.value,
  })
)

const canScrollPrev = ref(false)
const canScrollNext = ref(false)

const onInit = (embla: Ref<EmblaCarouselType | undefined>) => {
  if (embla.value) {
    emblaApi.value = embla.value
    updateButtonStates()

    emblaApi.value.on('select', () => {
      updateButtonStates()
    })
  }
}

const scrollPrev = () => {
  if (emblaApi.value) {
    emblaApi.value.scrollPrev()
    updateButtonStates()
  }
}

const scrollNext = () => {
  if (emblaApi.value) {
    emblaApi.value.scrollNext()
    updateButtonStates()
  }
}

const updateButtonStates = () => {
  if (!emblaApi.value) return

  if (!options.value.loop) {
    canScrollPrev.value = emblaApi.value.canScrollPrev()
    canScrollNext.value = emblaApi.value.canScrollNext()
  } else {
    canScrollPrev.value = true
    canScrollNext.value = true
  }
}

const handleClick = (item: galleryCard) => {
  clickedSlide.value = props.cardsSlideShow.indexOf(item)

  openDialog()
}

const titleStripTextPositionOptions = {
  center: 'align-class-center',
  left: 'align-class-left',
  right: 'align-class-right',
}

const cardTextPositionOptions = {
  center: 'text-center',
  left: 'text-start',
  right: 'text-end',
}

const isHovering = ref(false)

const hasTitleStrip = computed(() => {
  return props.titleStrip && Object.keys(props.titleStrip).length > 0
})

const mapItemAriaLabel = (item: galleryCard) => {
  const dynamicLabelParam =
    item.title ||
    (item.cardMediaSlide.media.type === 'image'
      ? item.cardMediaSlide.media.alt
      : '')

  return ts('accessibility.openGalleryImage', {
    label: dynamicLabelParam ?? '',
  })
}

onBeforeUnmount(() => {
  if (emblaApi.value) {
    emblaApi.value.off('select', updateButtonStates)
  }
})
</script>

<template>
  <div
    class="editorial-gallery-slideshow bg-background-secondary relative flex flex-col gap-6 overflow-hidden"
    :class="hasTitleStrip ? 'py-16' : ''"
  >
    <UITitleStrip
      v-if="hasTitleStrip"
      v-bind="titleStrip"
      :text-alignment="titleStripTextPositionOptions[titleTextAlign ?? 'left']"
    />
    <UISliderEditorialWithArrows
      :items="cardsSlideShow"
      :has-no-space-between="cardsConfiguration?.hasNoSpaceBetween"
      :loop="cardsSlideShow.length > 3"
      :show-navigation="cardsSlideShow.length > 3"
      :type="cardsConfiguration?.type"
      :card-dimensions="cardsConfiguration?.cardDimensions"
      :mobile-centered="cardsConfiguration?.mobileCentered"
      :padding="cardsConfiguration?.padding"
    >
      <template #default="{ item }">
        <button
          :aria-label="mapItemAriaLabel(item)"
          type="button"
          aria-haspopup="dialog"
          :aria-expanded="isDialogOpen"
          @click="handleClick(item)"
        >
          <UIMedia
            v-bind="item.cardMediaSlide"
            :class="[isHovering ? 'custom-zoom-cursor-add' : '']"
            class="image-slider-container"
            :aspect-ratio="
              aspectRatio ?? item.cardMediaSlide.aspectRatio ?? '4/5'
            "
            @mouseover="isHovering = true"
            @mouseleave="isHovering = false"
          />

          <div
            v-if="item.title || item.subtitle"
            class="slide-info pt-md lg:pt-lg flex w-full flex-col gap-2"
          >
            <UtilsMarkdown
              v-if="item.title"
              :content="item.title"
              is-preprocessed
              container="span"
              class="text-text-primary text-book-5"
              :class="[
                cardTextPositionOptions[item.cardTitlePosition ?? 'center'],
              ]"
            />
            <UtilsMarkdown
              v-if="item.subtitle"
              :content="item.subtitle"
              is-preprocessed
              container="span"
              class="text-text-primary text-light-5"
              :class="[
                cardTextPositionOptions[item.cardSubtitlePosition ?? 'center'],
              ]"
            />
          </div>
        </button>
      </template>
    </UISliderEditorialWithArrows>

    <OrganismsModal :id="GALLERY_SLIDE_SHOW_ID" :uses-highest-z-index="true">
      <template #body>
        <div class="relative h-full w-full">
          <button
            type="button"
            class="carousel-button carousel-button-prev hidden md:block"
            :disabled="!canScrollPrev"
            aria-label="Previous slide"
            @click.stop="scrollPrev"
          >
            <div class="arrow arrow--left"></div>
          </button>

          <UISliderBase :options="options" @init="onInit">
            <div v-for="(card, index) of cardsSlideShow" :key="index">
              <div
                class="flex h-[100vh] flex-col gap-8 pb-8 pt-12 md:pt-6 lg:pt-16"
              >
                <UIZoomableImage
                  v-if="card.cardMediaSlide.media.type === 'image'"
                  :src="card.cardMediaSlide.media.srcImage.secure_url!"
                  :alt="card.cardMediaSlide.media.alt!"
                  :reset-zoom="0"
                  :is-gallery-slide-show="true"
                  :aspect-ratio="card.cardMediaSlide.aspectRatio ?? '4/5'"
                  class="positive-padding"
                />
                <div
                  class="flex flex-col gap-2 px-2 md:m-auto md:w-[68vh] md:px-0"
                >
                  <UtilsMarkdown
                    v-if="card.title"
                    :content="card.title"
                    is-preprocessed
                    container="span"
                    class="text-text-primary text-book-5"
                    :class="[
                      cardTextPositionOptions[
                        card.zoomTitlePosition ?? 'center'
                      ],
                    ]"
                  />
                  <UtilsMarkdown
                    v-if="card.subtitle"
                    :content="card.subtitle"
                    is-preprocessed
                    container="span"
                    class="text-text-primary text-light-5"
                    :class="[
                      cardTextPositionOptions[
                        card.zoomSubtitlePosition ?? 'center'
                      ],
                    ]"
                  />
                </div>
              </div>
            </div>
          </UISliderBase>

          <button
            type="button"
            class="carousel-button carousel-button-next hidden md:block"
            :disabled="!canScrollNext"
            aria-label="Next slide"
            @click.stop="scrollNext"
          >
            <div class="arrow arrow--right"></div>
          </button>
        </div>
      </template>
    </OrganismsModal>
  </div>
</template>

<style scoped lang="scss">
.swiper-button-next-unique-gallery::after,
.swiper-button-prev-unique-gallery::after,
.swiper-button-next-unique-gallery,
.swiper-button-prev-unique-gallery {
  --swiper-navigation-size: 14px;
  --swiper-navigation-sides-offset: 30px;
  --swiper-navigation-top-offset: 50%;
  @apply text-text-primary;
}

.swiper-button-next-unique::after,
.swiper-button-prev-unique::after,
.swiper-button-next-unique,
.swiper-button-prev-unique {
  --swiper-navigation-size: 14px;
  --swiper-navigation-sides-offset: 10px;
  --swiper-navigation-top-offset: 45%;
  @apply text-text-primary;
}

.custom-zoom-cursor-add {
  cursor:
    url('@design-system/icons/AddWBkg.svg') 24 24,
    auto;
}

.carousel-button {
  @apply absolute top-1/2 z-50 -translate-y-1/2;
  @apply text-text-primary transition-opacity duration-300;
  @apply hover:opacity-80 disabled:opacity-30;
  @apply h-12 w-12 cursor-pointer;
  background: transparent;
  border: none;
  pointer-events: auto;

  &.carousel-button-prev {
    @apply left-8;
  }

  &.carousel-button-next {
    @apply right-8;
  }

  &:focus {
    outline: none;
  }

  &:not(:disabled):hover {
    @apply opacity-80;
  }
}

.arrow {
  width: 12px;
  height: 12px;
  border-top: 2px solid currentColor;
  border-right: 2px solid currentColor;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-135deg);
  pointer-events: none;

  &--right {
    transform: translate(-50%, -50%) rotate(45deg);
  }
}
</style>
